import { getTaskPriorityLabel, normalizeTaskPriority } from "./taskPriority";

// Agrupamento e ordenacao da central de tarefas. Extraido para ficar testavel e
// para a lista e o quadro concordarem sobre o que e "atrasada".

export const GROUP_BY = {
  NENHUM: "nenhum",
  ETAPA: "etapa",
  RESPONSAVEL: "responsavel",
  PRIORIDADE: "prioridade",
  PROJETO: "projeto",
  PRAZO: "prazo",
};

export const GROUP_BY_OPTIONS = [
  { value: GROUP_BY.NENHUM, label: "Sem agrupamento" },
  { value: GROUP_BY.PRAZO, label: "Prazo" },
  { value: GROUP_BY.RESPONSAVEL, label: "Responsavel" },
  { value: GROUP_BY.PRIORIDADE, label: "Prioridade" },
  { value: GROUP_BY.PROJETO, label: "Projeto" },
  { value: GROUP_BY.ETAPA, label: "Etapa" },
];

const BUCKET_LABELS = {
  atrasada: "Atrasadas",
  hoje: "Hoje",
  semana: "Esta semana",
  futuro: "Depois",
  "sem-prazo": "Sem prazo",
};

// Peso alto = mais urgente. Usado tanto para ordenar quanto para posicionar
// os grupos quando o agrupamento e por prazo.
const BUCKET_PESO = { atrasada: 4, hoje: 3, semana: 2, futuro: 1, "sem-prazo": 0 };

export function getDueBucket(valor) {
  if (!valor) return "sem-prazo";

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "sem-prazo";
  data.setHours(0, 0, 0, 0);

  if (data < hoje) return "atrasada";
  if (data.getTime() === hoje.getTime()) return "hoje";

  const limiteSemana = new Date(hoje);
  limiteSemana.setDate(limiteSemana.getDate() + 7);
  if (data <= limiteSemana) return "semana";

  return "futuro";
}

export function getDueBucketLabel(bucket) {
  return BUCKET_LABELS[bucket] || "Sem prazo";
}

export function estaAtrasada(task) {
  return !task?.concluido && getDueBucket(task?.vencimento) === "atrasada";
}

/** Urgencia primeiro, prioridade depois, prazo como desempate. */
export function ordenarTarefas(esquerda, direita) {
  const pesoEsq = BUCKET_PESO[getDueBucket(esquerda.vencimento)] || 0;
  const pesoDir = BUCKET_PESO[getDueBucket(direita.vencimento)] || 0;
  if (pesoEsq !== pesoDir) return pesoDir - pesoEsq;

  const prioridade =
    normalizeTaskPriority(esquerda.prioridade, 99) - normalizeTaskPriority(direita.prioridade, 99);
  if (prioridade !== 0) return prioridade;

  const dataEsq = esquerda.vencimento ? new Date(esquerda.vencimento).getTime() : Number.MAX_SAFE_INTEGER;
  const dataDir = direita.vencimento ? new Date(direita.vencimento).getTime() : Number.MAX_SAFE_INTEGER;
  return dataEsq - dataDir;
}

export function getResponsavelNome(task) {
  return task?.responsavel || task?.usuario_responsavel || null;
}

/**
 * Quebra as tarefas em grupos conforme o criterio escolhido.
 *
 * Devolve sempre [{ chave, titulo, cor, tarefas }]. Grupos "orfaos" (sem dono,
 * sem projeto, sem etapa) vao para o fim - sao o resto, nao a categoria.
 */
export function agruparTarefas(tarefas, criterio, contexto = {}) {
  const lista = [...tarefas].sort(ordenarTarefas);

  if (criterio === GROUP_BY.NENHUM || !criterio) {
    return [{ chave: "todas", titulo: null, cor: null, tarefas: lista }];
  }

  if (criterio === GROUP_BY.PRAZO) {
    const ordem = ["atrasada", "hoje", "semana", "futuro", "sem-prazo"];
    return ordem
      .map((bucket) => ({
        chave: bucket,
        titulo: getDueBucketLabel(bucket),
        cor: bucket === "atrasada" ? "#ef4444" : null,
        tarefas: lista.filter((task) => getDueBucket(task.vencimento) === bucket),
      }))
      .filter((grupo) => grupo.tarefas.length > 0);
  }

  if (criterio === GROUP_BY.PRIORIDADE) {
    return [1, 2, 3, 4]
      .map((nivel) => ({
        chave: String(nivel),
        titulo: getTaskPriorityLabel(nivel),
        cor: null,
        tarefas: lista.filter((task) => normalizeTaskPriority(task.prioridade, 99) === nivel),
      }))
      .filter((grupo) => grupo.tarefas.length > 0);
  }

  if (criterio === GROUP_BY.ETAPA) {
    const etapas = contexto.etapas || [];
    const grupos = etapas.map((etapa) => ({
      chave: String(etapa.id),
      titulo: etapa.descricao || etapa.nome || `Etapa ${etapa.id}`,
      cor: etapa.cor_hexa || null,
      tarefas: lista.filter((task) => String(task.id_etapa) === String(etapa.id)),
    }));

    const semEtapa = lista.filter(
      (task) => !task.id_etapa || !etapas.some((e) => String(e.id) === String(task.id_etapa))
    );

    if (semEtapa.length) {
      grupos.push({ chave: "sem-etapa", titulo: "Sem etapa", cor: null, tarefas: semEtapa });
    }

    return grupos.filter((grupo) => grupo.tarefas.length > 0);
  }

  // Responsavel e projeto seguem o mesmo formato: agrupa por id, ordena por
  // nome e joga os sem-vinculo para o fim.
  const porChave = new Map();
  const semVinculo = [];

  lista.forEach((task) => {
    const id =
      criterio === GROUP_BY.RESPONSAVEL ? task.id_usuario_responsavel : task.id_projeto;
    const nome =
      criterio === GROUP_BY.RESPONSAVEL ? getResponsavelNome(task) : task.projeto;

    if (!id) {
      semVinculo.push(task);
      return;
    }

    const chave = String(id);
    if (!porChave.has(chave)) {
      porChave.set(chave, {
        chave,
        titulo: nome || (criterio === GROUP_BY.RESPONSAVEL ? `Usuario ${id}` : `Projeto ${id}`),
        cor: null,
        tarefas: [],
      });
    }
    porChave.get(chave).tarefas.push(task);
  });

  const grupos = Array.from(porChave.values()).sort((a, b) =>
    a.titulo.localeCompare(b.titulo, "pt-BR")
  );

  if (semVinculo.length) {
    grupos.push({
      chave: "sem-vinculo",
      titulo: criterio === GROUP_BY.RESPONSAVEL ? "Sem responsavel" : "Sem projeto",
      cor: null,
      tarefas: semVinculo,
    });
  }

  return grupos;
}
